import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { X, Search, Sparkles, Pencil } from 'lucide-react'
import ModalTemplateCard from '@/components/common/ModalTemplateCard'
import { Template, ListTemplates } from '@/types'
import { useAppSelector } from '@/store'
import { selectHasStore, selectIsSuperAdmin, selectIsImpersonating } from '@/store/slices/authSlice'
import ConnectStoreModal from '@/components/common/ConnectStoreModal'
import { useIsShopifyStore } from '@/hooks/useIsShopifyStore'
import { usePermission } from '@/hooks/usePermission'
import { PERMISSIONS } from '@/features/auth/constants'

// Helper function to add static URL prefix to image URLs for specific template
const addStaticUrlToImageQuestions = (
    questions: any[],
    surveyId: string | null | undefined
) => {
    const TARGET_TEMPLATE_ID = 'eb4853bb-694f-4c12-8686-5b1bbf83ab17'
    const imageUrl1 = '/builder/pack1.png'
    const imageUrl2 = '/builder/pack2.png'

    // Early return if surveyId is null, undefined, or doesn't match target
    if (!surveyId || surveyId !== TARGET_TEMPLATE_ID) {
        return questions
    }

    // Early return if questions is not an array
    if (!Array.isArray(questions)) {
        return questions
    }

    return questions.map((question: any) => {
        if (
            question?.type === 'image' &&
            question?.config?.options &&
            Array.isArray(question.config.options)
        ) {
            return {
                ...question,
                config: {
                    ...question.config,
                    options: question.config.options.map(
                        (option: any, index: number) => ({
                            ...option,
                            imageUrl: index === 0 ? imageUrl1 : imageUrl2,
                        })
                    ),
                },
            }
        }
        return question
    })
}

// Extended template type that includes channels
type TemplateWithChannels = ListTemplates & {
    channels?: {
        pps?: {
            channel: 'pps'
            enabled: boolean
            distributionMethod: 'app-block' | 'embedded-link'
        }
        web?: {
            channel: 'web'
            enabled: boolean
            distributionMethod: 'link'
        }
        'on-site'?: {
            channel: 'on-site'
            enabled: boolean
            distributionMethod: string
        }
        whatsapp?: {
            channel: 'whatsapp'
            enabled: boolean
            distributionMethod: string
        }
    }
}
import CreateSurveyPreviewWrapper from './CreateSurveyPreviewWrapper'
import { useDispatch, useSelector } from 'react-redux'
import { useListTemplates } from '@/hooks/useListTemplates'
import { Spinner } from '@shopify/polaris'
import { useNavigate } from 'react-router-dom'
import { RootState } from '@/store'
import { setCreateModal } from '@/store/slices/surveySlice'
import { useAnalyticsClick } from '@/components/common/AnalyticsButton'
import { resetPreview, updatePreviewDetails } from '@/store/slices/preview'
import Cart from '@/assets/creation-modal/icons/Cart'
import Link from '@/assets/creation-modal/icons/Link'
import Onsite from '@/assets/creation-modal/icons/Onsite'
import Whatsapp from '@/assets/creation-modal/icons/Whatsapp'
import PenTool from '@/assets/creation-modal/icons/PenTool'

export default function CreateSurveyModal() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [previewSurvey, setPreviewSurvey] = useState<Template | null>(null)
    const [selectedCategory] = useState('All')
    const [selectedChannel, setSelectedChannel] = useState('pps')
    const [searchQuery, setSearchQuery] = useState('')
    const { data: templates, isLoading, isError, refetch } = useListTemplates()
    const hasStore = useAppSelector(selectHasStore)
    const isSuperAdmin = useAppSelector(selectIsSuperAdmin)
    const isImpersonating = useAppSelector(selectIsImpersonating)
    const [showStoreRequired, setShowStoreRequired] = useState(false)
    const createModal = useSelector(
        (state: RootState) => state.survey.createModal
    )
    const { trackClick } = useAnalyticsClick()
    const { isStandaloneStore } = useIsShopifyStore()
    const canWriteTemplates = usePermission(PERMISSIONS.TEMPLATES_WRITE)

    // Default to 'web' instead of 'pps' for standalone users when the modal opens
    useEffect(() => {
        if (createModal === 'create' && isStandaloneStore && selectedChannel === 'pps') {
            setSelectedChannel('web')
        }
    }, [createModal, isStandaloneStore])

    // Refetch templates when modal opens if data is missing (e.g. initial fetch failed)
    useEffect(() => {
        if (createModal === 'create' && !templates && !isLoading) {
            refetch()
        }
    }, [createModal, templates, isLoading, refetch])

    // Channel options
    const channels = [
        { id: 'pps', name: 'Shopify Post Purchase', icon: Cart, enabled: true, requiresShopify: true },
        { id: 'web', name: 'Share a link/ QR', icon: Link, enabled: true, requiresShopify: false },
        { id: 'on-site', name: 'On-site', icon: Onsite, enabled: true, requiresShopify: true },
        { id: 'whatsapp', name: 'Whatsapp', icon: Whatsapp, enabled: false, requiresShopify: false },
        // { id: 'email', name: 'Email', icon: Email, enabled: false },
    ]

    /** All survey creation requires a connected store (super admins bypass) */
    const needsStoreGate = !hasStore && !isSuperAdmin && !isImpersonating

    // Get dynamic heading based on selected channel
    const getChannelHeading = (channelId: string) => {
        const channelMap: Record<string, string> = {
            pps: 'Post Purchase Templates',
            web: 'Link Templates',

            'on-site': 'On-site Templates',
            whatsapp: 'WhatsApp Templates',
            email: 'Email Templates',
        }
        return channelMap[channelId] || 'Templates'
    }

    // Filter templates based on enabled channels first, then category and search query
    const filteredTemplates = (templates as TemplateWithChannels[])?.filter(
        (template) => {
            // First filter: only show templates that have the selected channel enabled
            if (
                template.channels &&
                template.channels[
                selectedChannel as keyof typeof template.channels
                ]
            ) {
                const channelConfig =
                    template.channels[
                    selectedChannel as keyof typeof template.channels
                    ]
                if (!channelConfig?.enabled) {
                    return false
                }
            }

            // Second filter: category
            const categoryMatch =
                selectedCategory === 'All' ||
                template.categoryTag === selectedCategory

            // Third filter: search query
            const searchMatch =
                searchQuery === '' ||
                template.name
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                template.description
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase())

            return categoryMatch && searchMatch
        }
    )

    return (
        <div className="flex flex-col items-center">
            <Dialog
                open={createModal === 'create'}
                onOpenChange={(value) => {
                    if (!value) dispatch(setCreateModal(null))
                }}
            >
                <DialogContent
                    className="max-w-[1212px] h-[85vh] min-h-[660px] px-0 pt-4 pb-0 shadow-none flex flex-col bg-[#FFFFFF] rounded-[20px] !gap-0 overflow-hidden"
                    hideClose
                >
                    {/* Header */}
                    <div className="px-6 pb-4 border-b border-gray-200 bg-white z-10 h">
                        <div className="flex items-center justify-start">
                            <h2 className="text-2xl font-[600] text-[#303030]">
                                Create a survey
                            </h2>
                            <div className="relative flex flex-1 ml-[70px]">
                                <Input
                                    placeholder="Search templates"
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                    className="pl-8 rounded-md border-gray-300"
                                />
                                <div className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                                    <Search
                                        className="w-4 h-4"
                                        stroke="#8A8A8A"
                                    />
                                </div>
                            </div>
                            <DialogClose className="ml-4 p-2 pr-0 rounded-full transition-colors">
                                <X className="w-5 h-5" stroke="#8A8A8A" />
                            </DialogClose>
                        </div>
                    </div>
                    <div className="flex flex-1 overflow-hidden">
                        {/* Left Sidebar - Channel Selection */}
                        <div className="w-[270px] h-full bg-[#FBFBF9]">
                            <div className="w-full py-3">
                                <h3 className="text-[16px] font-[600] text-[#303030] px-5 py-3">
                                    Pick a channel
                                </h3>
                                <div className="space-y-[6px] px-2">
                                    {channels.map((channel) => {
                                        const Icon = channel.icon
                                        const isSelected =
                                            selectedChannel === channel.id
                                        const isDisabled = !channel.enabled

                                        return (
                                            <button
                                                key={channel.id}
                                                onClick={() => {
                                                    if (channel.enabled) {
                                                        if (channel.requiresShopify && isStandaloneStore) {
                                                            setShowStoreRequired(true)
                                                        } else {
                                                            setSelectedChannel(
                                                                channel.id
                                                            )
                                                        }
                                                    }
                                                }}
                                                disabled={isDisabled}
                                                className={`w-full flex items-center justify-start gap-2 px-[14px] py-[10px] rounded-[12px] text-left transition-colors text-[14px] font-[550] ${isDisabled
                                                    ? 'text-gray-400 cursor-not-allowed opacity-60'
                                                    : isSelected
                                                        ? 'bg-[#FFFFFF] border border-[#EEEEEE]'
                                                        : 'text-gray-600 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Icon />
                                                    <span className="text-sm font-medium">
                                                        {channel.name}
                                                    </span>
                                                </div>
                                                {isDisabled && (
                                                    <span className="text-xs text-gray-400 font-[550] bg-[#E0F0FF] text-[#00527C] px-[6px] py-[2px] rounded-lg">
                                                        Coming Soon
                                                    </span>
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Main Content Area */}
                        <div className="flex-1 px-5 py-5 overflow-y-auto">
                            <div className="mb-6">
                                <h3 className="text-[16px] font-[600] text-[#303030] mb-4">
                                    {getChannelHeading(selectedChannel)}
                                </h3>

                                <button
                                    className="w-full flex items-center justify-start p-5 border-2 border-[#E5E5E5] border-dashed rounded-[10px] gap-5"
                                    onClick={() => {
                                        if (needsStoreGate) {
                                            setShowStoreRequired(true)
                                            return
                                        }
                                        if (['pps', 'on-site'].includes(selectedChannel) && isStandaloneStore) {
                                            setShowStoreRequired(true)
                                            return
                                        }
                                        trackClick('create_from_scratch')
                                        dispatch(resetPreview())
                                        dispatch(setCreateModal(null))
                                        navigate(
                                            `/surveys/scratch/${selectedChannel}`
                                        )
                                    }}
                                >
                                    <PenTool />
                                    <div className="flex flex-col gap-1 text-left">
                                        <h4 className="text-[16px] font-[600] text-[#303030]">
                                            Create from scratch
                                        </h4>
                                        <p className="text-[14px] font-[400] text-[#656565]">
                                            Customize your own survey
                                        </p>
                                    </div>
                                </button>

                                {/* Generate with AI Button */}
                                <button
                                    className="w-full flex items-center justify-start p-5 mt-4 border-2 border-[#E5E5E5] border-dashed rounded-[10px] gap-5"
                                    onClick={() => {
                                        if (needsStoreGate) {
                                            setShowStoreRequired(true)
                                            return
                                        }
                                        if (['pps', 'on-site'].includes(selectedChannel) && isStandaloneStore) {
                                            setShowStoreRequired(true)
                                            return
                                        }
                                        trackClick('create_from_scratch')
                                        dispatch(resetPreview())
                                        dispatch(setCreateModal(null))
                                        navigate('/surveys/ai-generate')
                                    }}
                                >
                                    <Sparkles className="w-6 h-6 text-[#303030]" />
                                    <div className="flex flex-col gap-1 text-left">
                                        <h4 className="text-[16px] font-[600] text-[#303030]">
                                            Generate with AI
                                        </h4>
                                        <p className="text-[14px] font-[400] text-[#656565]">
                                            Describe your survey and let AI build it
                                        </p>
                                    </div>
                                </button>

                                {isLoading ? (
                                    <div className="flex justify-center items-center h-64">
                                        <Spinner />
                                    </div>
                                ) : isError || !templates ? (
                                    <div className="flex flex-col items-center justify-center h-64 gap-3">
                                        <p className="text-sm text-gray-500">Failed to load templates</p>
                                        <button
                                            onClick={() => refetch()}
                                            className="text-sm text-blue-600 hover:underline"
                                        >
                                            Try again
                                        </button>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-3 gap-4 mt-5">
                                        {/* Template cards */}
                                        {filteredTemplates?.map(
                                            (card, index) => {
                                                return (
                                                    <div key={index} className="relative">
                                                        <ModalTemplateCard
                                                            card={card}
                                                            onClick={(card) => {
                                                                if (['pps', 'on-site'].includes(selectedChannel) && isStandaloneStore) {
                                                                    setShowStoreRequired(true)
                                                                    return
                                                                }
                                                                // Process questions to add static URL prefix for specific template
                                                                const processedQuestions =
                                                                    addStaticUrlToImageQuestions(
                                                                        card.questions,
                                                                        card.surveyId
                                                                    )

                                                                setPreviewSurvey({
                                                                    ...card,
                                                                    questions:
                                                                        processedQuestions,
                                                                })
                                                                dispatch(
                                                                    resetPreview()
                                                                )
                                                                dispatch(
                                                                    updatePreviewDetails(
                                                                        {
                                                                            name: card.name,
                                                                            questions:
                                                                                processedQuestions,
                                                                            thankYouCard: null
                                                                        }
                                                                    )
                                                                )
                                                                dispatch(
                                                                    setCreateModal(
                                                                        'preview'
                                                                    )
                                                                )
                                                            }}
                                                        />
                                                        {isSuperAdmin && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    dispatch(setCreateModal(null))
                                                                    navigate(`/builder/${card.surveyId}?isTemplate=true`)
                                                                }}
                                                                className="absolute top-3 right-3 p-1.5 rounded-md bg-white/90 border border-gray-200
                                                                    shadow-sm hover:bg-gray-50 hover:border-gray-300 z-10
                                                                    backdrop-blur-sm transition-colors duration-150"
                                                                title="Edit template"
                                                            >
                                                                <Pencil size={13} className="text-[#303030]" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )
                                            }
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Preview Dialog */}
            {createModal === 'preview' && (
                <CreateSurveyPreviewWrapper
                    open={createModal === 'preview'}
                    onOpenChange={() => {
                        dispatch(setCreateModal('create'))
                        dispatch(resetPreview())
                    }}
                    survey={previewSurvey}
                    selectedChannel={selectedChannel}
                    needsStoreGate={needsStoreGate}
                    onStoreRequired={() => setShowStoreRequired(true)}
                />
            )}

            {/* Store Required Modal */}
            <ConnectStoreModal
                open={showStoreRequired}
                onOpenChange={setShowStoreRequired}
                onConnectClick={() => dispatch(setCreateModal(null))}
            />
        </div>
    )
}
